import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import type { MoveOutInspectionReport } from '../types/moveOutInspection';

const toDate = (value: any): Date | undefined => {
  const date = value instanceof Date ? value : value?.toDate?.();
  return date instanceof Date && Number.isFinite(date.getTime()) ? date : undefined;
};

/** 입주기록과 점검 시각을 모두 대조해 다른 점검의 데이터를 섞지 않는다. */
export const loadMoveOutInspectionReport = async (
  fallback: MoveOutInspectionReport,
  inspectionId?: string
): Promise<MoveOutInspectionReport> => {
  const matches = (data: any) => data.inspectionType === 'move-out'
    && data.roomId === fallback.roomId && data.residencyId === fallback.residencyId
    && toDate(data.inspectedAt)?.getTime() === fallback.inspectedAt.getTime();

  let stored: { id: string; data: any } | undefined;
  if (inspectionId) {
    const snapshot = await getDoc(doc(db, 'portville_inspections', inspectionId));
    if (snapshot.exists() && matches(snapshot.data())) stored = { id: snapshot.id, data: snapshot.data() };
  }
  if (!stored) {
    // 과거 기록은 점검 ID가 없다. 단일 필드 조회로 기존 인덱스를 사용한다.
    const snapshot = await getDocs(query(collection(db, 'portville_inspections'), where('roomId', '==', fallback.roomId)));
    const candidates = snapshot.docs.filter(item => matches(item.data()));
    if (candidates.length > 1) throw new Error('동일한 시각의 점검 기록이 여러 건입니다. 기록을 확인해주세요.');
    if (candidates.length === 1) stored = { id: candidates[0].id, data: candidates[0].data() };
  }
  if (!stored) return fallback;

  const data = stored.data;
  const startDate = toDate(data.occupancyPeriod?.startDate);
  const endDate = toDate(data.occupancyPeriod?.endDate);
  return {
    ...fallback,
    id: stored.id,
    roomNumber: data.roomNumber != null ? String(data.roomNumber) : fallback.roomNumber,
    residentInfo: data.residentInfo ?? fallback.residentInfo,
    occupancyPeriod: startDate && endDate ? { startDate, endDate } : fallback.occupancyPeriod,
    inspectorName: data.inspectorName ?? fallback.inspectorName,
    inspectionItems: data.inspectionItems ?? fallback.inspectionItems,
    inspectionNotes: data.inspectionNotes ?? fallback.inspectionNotes,
    meterReadings: data.meterReadings ?? fallback.meterReadings,
    charges: data.charges ?? fallback.charges,
    signatureUrl: data.signatureUrl ?? fallback.signatureUrl
  };
};
